import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationExtras, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { ActivityMonitorService, ApiGatewayService, AppContextService, WindowService } from '@drc-eca/eca-components-lib';
import { OktaService } from '../../core/services/okta.service';
import { environment } from '../../../environments/environment';

const OKTA_TOKEN_STORAGE = 'okta-token-storage';

@Injectable({
  providedIn: 'root'
})
export class OktaReturnStateGuard  {
  private activityMonitorService = inject(ActivityMonitorService);
  private apiGatewayService = inject(ApiGatewayService);
  private appContextService = inject(AppContextService);
  private oktaService = inject(OktaService);
  private router = inject(Router);
  private windowService = inject(WindowService);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.activityMonitorService.stopMonitoring();
    const url = this.windowService['location'].toString();
    return this.checkUrl(url, route);
  }

  checkUrl(url: string, route: ActivatedRouteSnapshot): Observable<boolean> {
    const uri = new URL(url);
    // Okta will return with state
    const state = uri.searchParams.get('state');
    let client = '';
    let timeout = false;
    let returnUrl = '';
    if (route.queryParams['returnUrl']) {
      returnUrl = decodeURIComponent(decodeURIComponent(route.queryParams['returnUrl']));
    }
    if (state) {
      // if state is returned from Okta state should equal something like "client=DRCPORTAL;timeout=true"
      const segments = state.split(',');
      for (const segment of segments) {
        if (segment.includes('client')) {
          client = segment.split('=')[1];
        }
        if (segment.includes('timeout')) {
          timeout = segment.split('=')[1].toString() === 'true';
        }
        if (segment.includes('returnUrl')) {
          returnUrl = decodeURIComponent(decodeURIComponent(segment.split('=')[1]));
        }
      }
    }

    if (!client) {
      client = route.params['productCode'];
    }
    if (!timeout) {
      timeout = route.queryParams['timeout'] === 'true';
    }

    const oktaStorage = sessionStorage.getItem(OKTA_TOKEN_STORAGE);
    const oktaRedirectUrl = `${environment.okta.clientConfig.redirectUri}/${location.pathname}#/logout`.replace('//all', '/all');
    if (oktaStorage) {
      const state = [];
      if (client) {
        state.push(`client=${client}`);
      }
      if (timeout) {
        state.push(`timeout=${timeout}`);
      }
      if (returnUrl) {
        state.push(`returnUrl=${encodeURIComponent(returnUrl)}`);
      }
      // Okta will return here with it's session removed so no need to call ECA now.
      return from(this.oktaService.logout(oktaRedirectUrl, state)).pipe(map(() => false));
    } else {
      return this.logoutEca().pipe(
        map(() => {
          const navigate = ['login'];
          if (client) {
            navigate.push(client);
          }
          this.appContextService.setSelectedProductCode(client);
          if (!oktaStorage) {
            sessionStorage.clear();
          }
          this.navigate(navigate, timeout, returnUrl);
          // Don't actually allow the navigation to logout
          return false;
        })
      );
    }
  }

  private logoutEca() {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/logout`;

    return this.appContextService.getToken().pipe(
      switchMap(token => {
        if (token) {
          return this.apiGatewayService.post<any>(url, { token: token })
        }
        return of(token);
      }),
      catchError(() => of(undefined)),
      tap(() => this.appContextService.setToken(null as any))
    );
  }

  private navigate(url: string[], timeout: boolean, returnUrl: string) {
    const params = {
      queryParams: {}
    } as NavigationExtras;
    if (timeout) {
      params.queryParams!.timeout = true;
    }
    if (returnUrl) {
      params.queryParams!.returnUrl = returnUrl;
    }
    if (Object.keys(params.queryParams!).length) {
      this.router.navigate(url, params);
    } else {
      this.router.navigate(url);
    }
  }
}
